import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Paper,
  Alert,
} from '@mui/material';
import {
  Timer,
  ArrowBack,
  ArrowForward,
  Send,
  Flag,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Exam, Question, ExamAttempt, Course } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { toast } from 'react-hot-toast';

const TakeExam: React.FC = () => {
  const theme = useTheme();
  const { courseId, examId } = useParams<{ courseId: string; examId: string }>();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [exam, setExam] = useState<Exam | null>(null);
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const startExamSession = async () => {
      if (!courseId || !examId) return;
      
      try {
        setLoading(true);
        // Fetch course info
        const { results: allCourses } = await apiService.getPublishedCourses(1, 500);
        const courseData = allCourses.find(c => c.hashed === courseId);
        if (!courseData) throw new Error('Course not found');
        setCourse(courseData);

        // Start exam through API (or resume if active attempt exists)
        const { attempt: attemptData, questions: questionsData } = await apiService.startExam(examId);
        setAttempt(attemptData);
        setQuestions(questionsData);
        
        // Load saved answers if resuming an active attempt
        if (attemptData.user_answers && attemptData.user_answers.length > 0) {
          const savedAnswers: Record<number, number> = {};
          attemptData.user_answers.forEach((answer: any) => {
            if (answer.selected_choice_id) {
              savedAnswers[answer.question.id] = answer.selected_choice_id;
            }
          });
          setSelectedAnswers(savedAnswers);
          
          // Resume from the last answered question
          // Find the first unanswered question in the question_order
          const lastAnsweredIndex = attemptData.question_order.findIndex((qId: number) => {
            // Check if this question was NOT answered yet
            return !savedAnswers[qId];
          });
          
          // If all questions answered, go to last question; otherwise go to first unanswered
          const resumeIndex = lastAnsweredIndex === -1 
            ? attemptData.question_order.length - 1  // All answered, go to last
            : Math.max(0, lastAnsweredIndex);  // Go to first unanswered
          
          setCurrentQuestionIndex(resumeIndex);
          toast.success(`Resuming your exam... (Question ${resumeIndex + 1}/${attemptData.question_order.length})`);
        }
        
        // Setup timer if applicable
        if (attemptData.exam.duration_minutes) {
          const durationSeconds = attemptData.exam.duration_minutes * 60;
          
          // Calculate elapsed time if resuming
          const startTime = new Date(attemptData.started_at).getTime();
          const currentTime = Date.now();
          const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
          const remainingSeconds = Math.max(0, durationSeconds - elapsedSeconds);
          
          setTimeLeft(remainingSeconds);
          
          if (remainingSeconds === 0) {
            // Time already expired, auto-submit
            toast.error('Time expired! Submitting your exam...');
            setTimeout(() => handleAutoSubmit(), 1000);
          }
        }
      } catch (error) {
        console.error('Error starting exam:', error);
        toast.error('Could not start exam. Please try again.');
        navigate(`/courses/${courseId}`);
      } finally {
        setLoading(false);
      }
    };

    startExamSession();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [courseId, examId]);

  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => (prev !== null ? prev - 1 : null));
      }, 1000);
    } else if (timeLeft === 0) {
      handleAutoSubmit();
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timeLeft]);

  const handleAnswerSelect = async (questionId: number, choiceId: number) => {
    setSelectedAnswers(prev => ({ ...prev, [questionId]: choiceId }));
    
    // Save answer to backend immediately
    if (attempt) {
      try {
        await apiService.submitAnswer(attempt.id, questionId, choiceId);
      } catch (error) {
        console.error('Error saving answer:', error);
        toast.error('Failed to save answer. Please try again.');
      }
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleAutoSubmit = async () => {
    toast.error('Time is up! Submitting your exam automatically.');
    await submitExam();
  };

  const submitExam = async () => {
    if (!attempt) return;
    
    setSubmitting(true);
    try {
      // 1. Submit all remaining answers
      const answerPromises = Object.entries(selectedAnswers).map(([qId, cId]) => 
        apiService.submitAnswer(attempt.id, parseInt(qId), cId)
      );
      await Promise.all(answerPromises);

      // 2. Submit the main exam attempt
      const result = await apiService.submitExam(attempt.id);
      
      toast.success('Exam submitted successfully!');
      // Use attempt.id (the original attempt ID), not result.id (which is attempt_id in summary)
      navigate(`/courses/${courseId}/exams/${examId}/result/${attempt.id}`);
    } catch (error) {
      console.error('Error submitting exam:', error);
      toast.error('Error submitting exam. Please check your connection.');
    } finally {
      setSubmitting(false);
      setIsSubmitDialogOpen(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) return <LoadingSpinner message="Preparing your exam questions..." />;
  if (!attempt || questions.length === 0) return <Alert severity="error">No questions found for this exam.</Alert>;

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Header Info */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {attempt.exam.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Question {currentQuestionIndex + 1} of {questions.length}
          </Typography>
        </Box>

        {timeLeft !== null && (
          <Box sx={{ 
            p: 1.5, 
            px: 3, 
            borderRadius: 3, 
            backgroundColor: timeLeft < 60 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
            border: `1px solid ${timeLeft < 60 ? '#ef4444' : '#10b981'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Timer sx={{ color: timeLeft < 60 ? '#ef4444' : '#10b981' }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: timeLeft < 60 ? '#ef4444' : '#10b981', fontFamily: 'monospace' }}>
              {formatTime(timeLeft)}
            </Typography>
          </Box>
        )}
      </Box>

      <LinearProgress 
        variant="determinate" 
        value={progress} 
        sx={{ 
          height: 8, 
          borderRadius: 4, 
          mb: 4,
          backgroundColor: 'rgba(0,0,0,0.05)',
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(45deg, #10b981 30%, #22c55e 90%)',
          }
        }} 
      />

      {/* Question Card */}
      <Card sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', overflow: 'visible', position: 'relative' }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          {/* Render question text as HTML (supports Summernote formatting) */}
          <Box 
            sx={{ 
              mb: 4,
              '& p': { 
                fontSize: '1.25rem',
                fontWeight: 600,
                lineHeight: 1.6,
                marginBottom: '0.5rem',
                color: 'text.primary'
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                marginTop: '1rem',
                marginBottom: '1rem',
                display: 'block',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              },
              '& ul, & ol': {
                paddingLeft: '1.5rem',
                marginBottom: '1rem'
              },
              '& li': {
                marginBottom: '0.5rem'
              },
              '& strong': {
                fontWeight: 700
              },
              '& em': {
                fontStyle: 'italic'
              }
            }}
            dangerouslySetInnerHTML={{ __html: currentQuestion.question_text }}
          />

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedAnswers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerSelect(currentQuestion.id, parseInt(e.target.value))}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {currentQuestion.choices?.map((choice) => (
                  <Paper
                    key={choice.id}
                    elevation={0}
                    sx={{
                      p: 1,
                      px: 2,
                      border: '1px solid',
                      borderColor: selectedAnswers[currentQuestion.id] === choice.id ? '#10b981' : 'rgba(0,0,0,0.1)',
                      backgroundColor: selectedAnswers[currentQuestion.id] === choice.id ? '#10b981' : 'white',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: '#10b981',
                        backgroundColor: '#10b981',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                        '& *': {
                          color: '#ffffff !important', // White text on hover
                        }
                      }
                    }}
                  >
                    <FormControlLabel
                      value={choice.id}
                      control={<Radio color="success" sx={{ color: selectedAnswers[currentQuestion.id] === choice.id ? '#ffffff' : 'inherit' }} />}
                      label={
                        <Box 
                          sx={{ 
                            width: '100%',
                            color: selectedAnswers[currentQuestion.id] === choice.id ? '#ffffff' : '#1e293b',
                            '& *': { 
                              color: selectedAnswers[currentQuestion.id] === choice.id ? '#ffffff !important' : '#1e293b !important',
                            },
                            '& p': { 
                              margin: 0,
                              fontSize: '1.05rem',
                              fontWeight: 500
                            },
                            '& strong': {
                              fontWeight: 700
                            },
                            '& img': {
                              maxWidth: '100%',
                              height: 'auto',
                              borderRadius: '6px',
                              marginTop: '0.5rem',
                              marginBottom: '0.5rem',
                              display: 'block'
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: choice.choice_text }}
                        />
                      }
                      sx={{ 
                        width: '100%', 
                        m: 0,
                        alignItems: 'flex-start',
                        '& .MuiFormControlLabel-label': { 
                          width: '100%',
                          pt: 0.5
                        }
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          disabled={currentQuestionIndex === 0}
          variant="outlined"
          sx={{ px: 4, borderRadius: 2, height: 48 }}
        >
          Previous
        </Button>

        {currentQuestionIndex === questions.length - 1 ? (
          <Button
            variant="contained"
            color="success"
            endIcon={<Send />}
            onClick={() => setIsSubmitDialogOpen(true)}
            sx={{ 
              px: 6, 
              borderRadius: 2, 
              height: 48,
              background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
              fontWeight: 700,
              boxShadow: '0 8px 16px rgba(16, 185, 129, 0.2)',
            }}
          >
            Review & Submit
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleNext}
            sx={{ px: 4, borderRadius: 2, height: 48 }}
          >
            Next Question
          </Button>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog 
        open={isSubmitDialogOpen} 
        onClose={() => setIsSubmitDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
          Finish Exam?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" textAlign="center">
            You have answered {Object.keys(selectedAnswers).length} out of {questions.length} questions.
          </Typography>
          {Object.keys(selectedAnswers).length < questions.length && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Wait! You still have unanswered questions.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button onClick={() => setIsSubmitDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2, px: 3 }}>
            Keep Working
          </Button>
          <Button 
            onClick={submitExam} 
            variant="contained" 
            color="success" 
            disabled={submitting}
            sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}
          >
            {submitting ? 'Submitting...' : 'Yes, Submit Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TakeExam;
